import { Box, Button, IconButton, MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import type { GridSortModel } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { SORTABLE_FIELDS } from '../constants';
import DeleteIcon from '@mui/icons-material/Delete';
import { SortItemDraft } from '~/components/ApplicationReports/types.ts';
import { styles } from '~/components/ApplicationReports/styles.ts';
interface MultiSortPanelProps {
  sortModel: GridSortModel[number][];
  onChange: (model: GridSortModel[number][]) => void;
  disabled?: boolean;
}

export const MultiSortPanel = ({ sortModel, onChange, disabled }: MultiSortPanelProps) => {
  const [internalModel, setInternalModel] = useState<SortItemDraft[]>(
    sortModel.map((s) => ({ ...s, sort: s.sort ?? 'asc' }))
  );

  useEffect(() => {
    setInternalModel(sortModel.map((s) => ({ ...s, sort: s.sort ?? 'asc' })));
  }, [sortModel]);

  const usedFields = internalModel.map((s) => s.field).filter(Boolean);

  const handleAddSort = () => {
    setInternalModel((prev) => [...prev, { field: '', sort: 'asc', isDraft: true }]);
  };

  const handleChangeField = (index: number, field: string) => {
    const updated = internalModel.map((item, i) =>
      i === index ? { ...item, field, isDraft: false } : item
    );
    setInternalModel(updated);

    const allFilled = updated.every((item) => item.field);
    if (allFilled) {
      onChange(updated.map(({ field, sort }) => ({ field, sort })));
    }
  };

  const handleChangeDirection = (index: number, sort: 'asc' | 'desc') => {
    const updated = internalModel.map((item, i) => (i === index ? { ...item, sort } : item));
    setInternalModel(updated);

    const allFilled = updated.every((item) => item.field);
    if (allFilled) {
      onChange(updated.map(({ field, sort }) => ({ field, sort })));
    }
  };

  const handleRemove = (index: number) => {
    const updated = internalModel.filter((_, i) => i !== index);
    setInternalModel(updated);

    const filled = updated.filter((item) => item.field);
    onChange(filled.map(({ field, sort }) => ({ field, sort })));
  };

  const availableFields = (currentField: string) =>
    SORTABLE_FIELDS.filter((f) => !usedFields.includes(f.field) || f.field === currentField);

  const canAddMore =
    usedFields.length < SORTABLE_FIELDS.length &&
    !internalModel.some((item) => item.isDraft || !item.field);

  return (
    <Box sx={styles.sort}>
      {internalModel.map((item, index) => (
        <Box key={index} sx={styles.sortTable}>
          <Typography sx={styles.sortTableText}>{index === 0 ? 'Спочатку' : 'Потім'}</Typography>

          <Select
            size="small"
            value={item.field}
            displayEmpty
            disabled={disabled}
            onChange={(e) => handleChangeField(index, e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="" disabled>
              Оберіть колонку
            </MenuItem>
            {availableFields(item.field).map((f) => (
              <MenuItem key={f.field} value={f.field}>
                {f.label}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={item.sort}
            disabled={disabled || !item.field}
            onChange={(e) => handleChangeDirection(index, e.target.value as 'asc' | 'desc')}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="asc">За зростанням</MenuItem>
            <MenuItem value="desc">За спаданням</MenuItem>
          </Select>

          <IconButton size="small" onClick={() => handleRemove(index)} disabled={disabled}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ))}

      {canAddMore && (
        <Button
          startIcon={<AddIcon />}
          size="small"
          variant="text"
          onClick={handleAddSort}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start' }}
        >
          Додати сортування
        </Button>
      )}
    </Box>
  );
};
